import { fromJS } from "immutable"
import { createSelector, createStructuredSelector } from "reselect"
import { hasAccessToMultipleCountries } from "shared/imports/regionConstants"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import constants from "subfeatures/RegionSelectionPage/constants"
import { STATES_IN_SESSION_NAME, STATES_OUTTA_SESSION_NAME } from "shared/imports/sharedConstants"
import { getRegionAbbrevValueMapping } from "subfeatures/RegionSelectionPage/helperFunctions"
import { isRegionDisabled, isRegionEnabled, isRegionCustom } from "shared/region-selection/helperFunctions"
import { mobileBreakpoint } from "app/static/frontend/design-constants"

// get RegionSelector slice of store
export const getRegionSelectionPage = (state) => state.regionSelectionPage
export const getCustomRegionForm = (state) => state.form.get("customRegionForm", fromJS({}))

const regionAbbrevValueMapping = getRegionAbbrevValueMapping()

/**
 * Creates a simple selector for getting value from
 * regionSelector store
 *
 * @param {String} key string of key to get
 * @returns {Function} returns the select function
 */
const createRegionSelectorSelector = (key) =>
    createSelector(getRegionSelectionPage, (regionSelector) => regionSelector.get(key))

// converts immutable values to non-immutable
const createRegionSelectorImmutableSelector = (key) =>
    createSelector(createRegionSelectorSelector(key), (immutableValue) => immutableValue.toJS())

// basic selectors
export const selectSearchTerm = createRegionSelectorSelector("searchTerm")
export const selectIsLoading = createRegionSelectorSelector("isLoading")
export const selectCustomRegions = createRegionSelectorImmutableSelector("customRegionSelections")
export const selectPageIsOpen = createRegionSelectorSelector("pageIsOpen")

/**
 * Icons for all the major region gruopings found in SupportedRegion -- will move this into the SupportedRegion enum when no
 * longer feature flagging all of this.
 */
export const majorRegionIcons = {
    [DjangIO.app.models.SupportedRegion.all_regions.value]: { icon_element: constants.allIconString },
    [DjangIO.app.models.SupportedRegion.federal.value]: { icon: "bank" },
    [DjangIO.app.models.SupportedRegion.states_and_local.value]: { icon_element: constants.allIconString },
    [DjangIO.app.models.SupportedRegion.states.value]: { icon: "compass" },
    [DjangIO.app.models.SupportedRegion.local.value]: { icon: "building" },
    [DjangIO.app.models.SupportedRegion.eu.value]: { icon_element: constants.euSVG },
    [DjangIO.app.models.SupportedRegion.user_global.value]: { icon: "globe" },
    [DjangIO.app.models.SupportedRegion.international.value]: { icon: "map-o" },
    [DjangIO.app.models.SupportedRegion.eu_member_states.value]: { icon_element: constants.euSVG },
}

const majorRegionDisabledTooltip = (region) =>
    `Your organization does not currently have access to ${region.region_name}.
    To learn more about this product, please email products@quorum.us${
        !!constants.PRODUCT_PAGE_PATHS[region.value]
            ? " or click the region to visit our product page and learn more"
            : ""
    }.`

const createMajorRegionTooltip = (region) =>
    isRegionDisabled(region) ? majorRegionDisabledTooltip(region) : constants.MAJOR_REGION_TOOLTIPS[region.value]

// top level groups: Global, Entire US, US Federal, US States & Local, US State, US Local, EU, EU Member States
const { SupportedRegion } = DjangIO.app.models
export const selectMajorRegionsToDisplay = () =>
    [
        // using label and order field to overwrite the SupportedRegion fields while this is still feature flagged
        // order (which will probably change): Global, Entire US, Federal, States and Local, States, Local, EU, EU Members
        ...(isFeatureEnabled("ff_global_region") && hasAccessToMultipleCountries()
            ? [
                  {
                      ...SupportedRegion.user_global,
                      region_name: "Global",
                      order: 2,
                  },
              ]
            : []),
        {
            ...SupportedRegion.all_regions,
            region_name: "Entire US",
            order: 3,
        },
        {
            ...SupportedRegion.federal,
            region_name: "US Federal",
            order: 4,
        },
        {
            ...SupportedRegion.states,
            region_name: "US States",
            order: 5,
        },
        {
            ...SupportedRegion.local,
            region_name: "US Local",
            order: 6,
        },
        {
            ...SupportedRegion.states_and_local,
            region_name: "US States & Local",
            order: 7,
        },
        {
            ...SupportedRegion.eu,
            order: 8,
        },
    ]
        .concat([
            {
                ...SupportedRegion.international,
                region_name: "International",
                order: 9,
            },
            {
                ...SupportedRegion.eu_member_states,
                order: 10,
            },
        ])

        // add the icon, tooltip text, and url to corresponding product page
        .map((region) => ({
            ...region,
            ...majorRegionIcons[region.value],
            tooltipText: createMajorRegionTooltip(region),
            productPagePath: constants.PRODUCT_PAGE_PATHS[region.value],
        }))
        .sort((a, b) => a.order - b.order)

const selectCustomRegionsToDisplay = createSelector(selectCustomRegions, (customRegions) => {
    const sortByRegionName = (name) => (a, b) => {
        if (a.name === name) {
            return -1
        }
        return b.name === name ? 1 : 0
    }

    const sortedRegions = customRegions
        .map((customReg) => ({
            label: customReg.name,
            value: `custom-${customReg.name}`,
            icon: "star",
            disabledRegion: false,
            ...customReg,
        }))
        // Sort STATES_IN_SESSION_NAME to be in the front of the list, followed by outta session
        .sort(sortByRegionName(STATES_OUTTA_SESSION_NAME))
        .sort(sortByRegionName(STATES_IN_SESSION_NAME))

    return sortedRegions
})

const sortAlphabetically = (reg1, reg2) => (reg1.region_name.toLowerCase() < reg2.region_name.toLowerCase() ? -1 : 1)
export const formatRegion = (reg) => ({
    ...reg,
    label: reg.region_name,
    disabled: isRegionDisabled(reg) && !majorRegionIcons[reg.value],
    disabledRegion: isRegionDisabled(reg) && !!majorRegionIcons[reg.value],
})

export const selectAllowedRegions = () =>
    []
        // major regions
        .concat(selectMajorRegionsToDisplay())
        // all other regions
        .concat(DjangIO.app.models.Region.items().sort(sortAlphabetically))
        .filter((region) => isRegionEnabled(region))

export const regionGroupings = createSelector(
    selectMajorRegionsToDisplay,
    selectCustomRegionsToDisplay,
    selectIsLoading,
    (majorRegions, customRegions, isLoading) => {
        const { SupportedRegion } = DjangIO.app.models

        const regionGroups = [
            // most common top-level groups
            {
                groupLabel: "Major Regions",
                key: "top-level-groupings",
                regions: majorRegions.map(formatRegion),
            },
            // international
            {
                groupLabel: SupportedRegion.international.group_label,
                key: SupportedRegion.international.key,
                regions: SupportedRegion.international.child_regions
                    .map((region) => SupportedRegion.by_value(region))
                    // Until all international regions are ready, we want to hide unpermissioned international regions
                    // We currently can't answer questions in the sales process about when it'll be ready
                    .filter((region) => isRegionEnabled(region))
                    .sort(sortAlphabetically)
                    .map(formatRegion)
                    // add a stand in icon while international is still for Q employees only
                    .map((region) => ({ ...region, icon: "globe" })),
            },
            // stateRegions
            {
                groupLabel: SupportedRegion.states.group_label,
                key: SupportedRegion.states.key,
                regions: SupportedRegion.states.child_regions
                    .map((region) => SupportedRegion.by_value(region))
                    .sort(sortAlphabetically)
                    .map(formatRegion),
            },
            // localRegions
            {
                groupLabel: SupportedRegion.local.group_label,
                key: SupportedRegion.local.key,
                regions: SupportedRegion.local.child_regions
                    .map((region) => SupportedRegion.by_value(region))
                    .filter((region) => region.show_in_selector)
                    .sort(sortAlphabetically)
                    .map(formatRegion),
            },
            // south america
            // asia pacific
            // antarctica
        ]

        // custom regions - show after majorRegions when finished loading
        if (!isLoading) {
            regionGroups.splice(1, 0, {
                groupLabel: "Custom Regions",
                key: "custom-regions",
                regions: customRegions,
                showManageCustomRegions: window.innerWidth > mobileBreakpoint,
            })
        }

        return regionGroups
    },
)

export const getRegionTooltipText = (region) => {
    if (region.tooltipText) {
        return region.tooltipText
    } else if (region.child_regions) {
        // SupportedRegion has child_regions = list of region values
        return region.child_regions
            .filter((value) => isRegionEnabled(value))
            .map((value) => DjangIO.app.models.SupportedRegion.by_value(value).region_name)
            .join(", ")
    } else if (region.regions) {
        // custom region has regions = list of region abbrevs
        return region.regions
            .map((field) => DjangIO.app.models.SupportedRegion.by_value(regionAbbrevValueMapping[field]).region_name)
            .join(", ")
    }

    // is a single region, so nothing interesting to put in the tooltip
    return undefined
}

const regionSelectionPageSelectors = createStructuredSelector({
    searchTerm: selectSearchTerm,
    customRegionSelections: selectCustomRegions,
    isLoading: selectIsLoading,
    regionGroupings,
    majorRegions: selectMajorRegionsToDisplay,
    isRegionCustom,
    isRegionDisabled,
    getRegionTooltipText: () => getRegionTooltipText,
    allowedRegions: selectAllowedRegions,
})

export default regionSelectionPageSelectors

// For the button in the top left nav bar which has some special logic when the RegionSelectionPage is open

export const regionNavBarConnector = createStructuredSelector({
    pageIsOpen: selectPageIsOpen,
})

// CustomRegionForm specific

export const name = createSelector(getCustomRegionForm, (form) => form.getIn(["values", "name"]))

export const regions = createSelector(getCustomRegionForm, (form) =>
    form.getIn(["values", "regions"], fromJS([])).toJS(),
)

export const initialValues = createSelector(
    (state, props) => props,
    selectCustomRegions,
    (props, customRegions) => {
        if (props.params.datumId && !props.isLoading) {
            const existingCustomRegion = customRegions.filter(({ id }) => `${id}` === props.params.datumId) // props.params.datumId will be a string)
            if (existingCustomRegion.length === 1) {
                return {
                    name: existingCustomRegion[0].name,
                    // regions is a list of region abbrevs and AllRegionSelect needs Region enum values
                    regions: existingCustomRegion[0].regions.map((abbrev) => regionAbbrevValueMapping[abbrev]),
                }
            }
        }
        return {
            name: "",
            regions: [],
        }
    },
)

export const customRegionFormConnection = createStructuredSelector({
    initialValues,
    regions,
    isLoading: selectIsLoading,
})
